"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  SubscriptionStatus,
  WorkspaceSubscription,
  checkSubscriptionAccess,
} from "@/app/actions/subscription.action";
import { useOrganization } from "@clerk/nextjs";
import { useSearchParams } from "next/navigation";

type SubscriptionContextType = {
  hasAccess: boolean;
  workspace: {
    id: string;
    slug: string;
    name: string;
    stripeCustomerId?: string;
    subscriptionId?: string;
    subscriptionStatus?: SubscriptionStatus;
    trialEndsAt?: string;
    currentPeriodEndsAt?: string;
    activeUserCount: number;
    canceledAt?: string;
    exemptFromBilling?: boolean;
  } | null;
  isLoading: boolean;
  error: Error | null;
  isTrialing: boolean;
  isActive: boolean;
  isCanceled: boolean;
  isUnpaid: boolean;
  isExempt: boolean;
  isTrialExpired: boolean;
  isPeriodExpired: boolean;
  trialEndsAt: Date | null;
  currentPeriodEndsAt: Date | null;
  refetch: () => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { organization } = useOrganization();
  const searchParams = useSearchParams();
  const [subscription, setSubscription] =
    useState<WorkspaceSubscription | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchedOrgId, setFetchedOrgId] = useState<string | null>(null);

  // Success param check
  const isSuccess = searchParams?.get("subscription") === "success";

  const fetchSubscriptionData = async (force = false) => {
    if (!organization?.id) return;

    // Skip if we've already fetched for this organization, unless forced
    if (!force && fetchedOrgId === organization.id) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(`Fetching subscription data for org ${organization.id}`);
      // Call the action to check subscription access
      const data = await checkSubscriptionAccess(organization.id);
      setSubscription(data);
      setFetchedOrgId(organization.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch subscription data")
      );
      // Fallback to give access on error to avoid locking users out
      setSubscription({ hasAccess: true, workspace: null });
      setFetchedOrgId(organization.id);
    } finally {
      setIsLoading(false);
    }
  };

  // Check for initial data fetch or organization change
  useEffect(() => {
    if (organization?.id && fetchedOrgId !== organization.id) {
      fetchSubscriptionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  // Refetch when success param is present
  useEffect(() => {
    if (isSuccess) {
      fetchSubscriptionData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  // Calculate date-based status flags
  const now = new Date();
  const trialEndsAt = subscription?.workspace?.trialEndsAt
    ? new Date(subscription.workspace.trialEndsAt)
    : null;
  const currentPeriodEndsAt = subscription?.workspace?.currentPeriodEndsAt
    ? new Date(subscription.workspace.currentPeriodEndsAt)
    : null;

  // Check if trial is expired
  const isTrialExpired = trialEndsAt ? now > trialEndsAt : false;

  // Check if billing period is expired
  const isPeriodExpired = currentPeriodEndsAt
    ? now > currentPeriodEndsAt
    : false;

  // Calculate derived status flags
  const subscriptionStatus = subscription?.workspace?.subscriptionStatus;

  const isTrialing = subscriptionStatus === "trialing" && !isTrialExpired;
  const isActive = subscriptionStatus === "active";
  const isCanceled = subscriptionStatus === "canceled";
  const isUnpaid = subscriptionStatus === "unpaid";
  const isExempt = subscription?.workspace?.exemptFromBilling || false;

  // Calculate effective access
  const hasEffectiveAccess =
    isExempt ||
    isActive ||
    (subscriptionStatus === "trialing" && !isTrialExpired) ||
    ((subscriptionStatus === "canceled" || subscriptionStatus === "unpaid") &&
      !isPeriodExpired);

  const value = {
    hasAccess: hasEffectiveAccess,
    workspace: subscription?.workspace || null,
    isLoading,
    error,
    isTrialing,
    isActive,
    isCanceled,
    isUnpaid,
    isExempt,
    isTrialExpired,
    isPeriodExpired,
    trialEndsAt,
    currentPeriodEndsAt,
    refetch: () => fetchSubscriptionData(true),
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
}
